import { ComponentBase, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { autoinject } from 'aurelia-framework';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { Toaster } from 'app/shared/lib/toaster';
import { deepClone, forceNull } from 'app/shared/lib/utilities';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { EditMemberAddress } from 'app/core/components/members/edit-member-address';
import { compareObjects } from 'app/shared/lib/compare-objects';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions';
import { CustomFieldService, ICustomFieldSearchFilters } from 'app/core/services/custom-field-service';
import { EventAggregator } from 'aurelia-event-aggregator';
import { MemberDisplayChangeEvent } from 'app/cd/lib/events';
import * as CD from "app/cd/models/essette-api";

@autoinject()
export class MemberInfoPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected validateForm = validate;
    protected memberId: string | null = null;

    protected originalMember: CD.Member | null = null;
    protected originalAddresses: Array<CD.MemberAddress> = [];

    protected saveMember: CD.Member | null = null;
    protected saveMemberDemographics: CD.MemberDemographics | null = null;
    protected saveMemberAddresses: Array<CD.MemberAddress> = [];
    protected saveMemberPhoneNumbers: CD.MemberPhoneNumberContainer | null = null;
    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;
    protected searchFilters: ICustomFieldSearchFilters = { targetCode: 'CD.Member' };
    protected memberUDFCount: number = 0;

    ////****************** Element References ***************************************////
    protected $editMemberAddresses!: EditMemberAddress;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly memberService: MemberService,
        private readonly customFieldContext: CustomFieldContext,
        private readonly customFieldService: CustomFieldService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }


    protected activate({ memberId }: { memberId: string }): void {
        this.memberId = memberId;
        this.reload();
    }

    ////****************** Functions ************************************************////

    protected async reload(): Promise<void> {
        await this.loadMember();
        await this.loadCustomFieldTargets();
        await this.loadMemberDefinitions();

        //set Original Member and Address Objects for comparisons during submits
        if(this.saveMember) {
            this.originalMember = deepClone(this.saveMember);
            this.originalAddresses = deepClone(this.saveMemberAddresses)
        }
    }

    protected cancel() {
        this.reload();
    }

    protected async submit(): Promise<void> {
        this.state.beginSave();
        await this.loadCustomFieldTargets();

        await this.saveMemberInformation();
        await this.saveDemographics();
        await this.savePhoneNumber(this.originalMember?.phoneNumber?.home!, this.saveMemberPhoneNumbers?.home!, 'home');
        await this.savePhoneNumber(this.originalMember?.phoneNumber?.cell!, this.saveMemberPhoneNumbers?.cell!, 'cell');
        await this.savePhoneNumber(this.originalMember?.phoneNumber?.work!, this.saveMemberPhoneNumbers?.work!, 'work');
        await this.savePhoneNumber(this.originalMember?.phoneNumber?.fax!, this.saveMemberPhoneNumbers?.fax!, 'fax');

        await this.reload();
        this.state.endSave();
    }

    protected buildMember(member: CD.Member): CD.Member {
        const saveMember: CD.Member = {
            id: member.id,
            // Member Information
            identifier: {
                medicaid: forceNull(member.identifier?.medicaid),
                socialSecurityNumber: forceNull(member.identifier?.socialSecurityNumber),
                external: forceNull(member.identifier?.external)
            },
            name: {
                first: forceNull(member.name?.first),
                middle: forceNull(member.name?.middle),
                last: forceNull(member.name?.last)
            },
            birthDate: member.birthDate,
            deathDate: member.deathDate,
            emailAddress: forceNull(member.emailAddress),
            // Member UDFs
            custom: {
                text: this.parseTextContainer(member.custom?.text),
                lookup: this.parseLookupContainer(member.custom?.lookup)
            }
        };

        // for these lookup values, only send values that are being changed
        if(this.originalMember?.gender?.id !== member.gender?.id) saveMember.gender = member.gender ? { code: member.gender.code } : null;
        if(this.originalMember?.maritalStatus?.id !== member.maritalStatus?.id) saveMember.maritalStatus = member.maritalStatus ? { code: member.maritalStatus.code } : null;
        if(this.originalMember?.race?.id !== member.race?.id) saveMember.race = member.race ? { code: member.race.code } : null;
        if(this.originalMember?.ethnicity?.id !== member.ethnicity?.id) saveMember.ethnicity = member.ethnicity ? { code: member.ethnicity.code } : null;
        if(this.originalMember?.language?.reads?.id !== member.language?.reads?.id || this.originalMember?.language?.speaks?.id !== member.language?.speaks?.id) {
            saveMember.language = {};
            if(this.originalMember?.language?.reads?.id !== member.language?.reads?.id) saveMember.language.reads = member.language?.reads ? { code: member.language.reads.code } : null;
            if(this.originalMember?.language?.speaks?.id !== member.language?.speaks?.id) saveMember.language.speaks = member.language?.speaks ? { code: member.language.speaks.code } : null;
        }
        return saveMember;
    }

    protected buildMemberDemographics(memberDemographics: CD.MemberDemographics): CD.MemberDemographics {
        const saveMemberDemographics: CD.MemberDemographics = {
            member: { id: this.memberId },
            pronouns: forceNull(memberDemographics.pronouns),
            preferredName: forceNull(memberDemographics.preferredName),
            disability: forceNull(memberDemographics.disability),
            hasDurablePowerOfAttorney: memberDemographics.hasDurablePowerOfAttorney,
            hasAdvanceDirective: memberDemographics.hasAdvanceDirective,
            advanceDirectiveExpirationDate: memberDemographics.advanceDirectiveExpirationDate
        };

        // for these lookup values, only send values that are being changed
        if(this.originalMember?.demographics?.gender?.id !== memberDemographics.gender?.id) saveMemberDemographics.gender = memberDemographics.gender ? { code: memberDemographics.gender.code } : null;
        if(this.originalMember?.demographics?.race?.id !== memberDemographics.race?.id) saveMemberDemographics.race = memberDemographics.race ? { code: memberDemographics.race.code } : null;

        return saveMemberDemographics;
    }

    protected buildPhoneNumber(phoneNumber: CD.MemberPhoneNumber, code: string): CD.MemberPhoneNumber {
        let typeCode;
        switch(code) {
            case 'home':
                typeCode = "ESSETTE:CD.Member.Home";
                break;
            case 'cell':
                typeCode = "ESSETTE:CD.Member.Cell";
                break;
            case 'work':
                typeCode = "ESSETTE:CD.Member.Work";
                break;
            case 'fax':
                typeCode = "ESSETTE:CD.Member.Fax";
                break;
        }

        const savePhoneNumber: CD.MemberPhoneNumber = {
            id: phoneNumber.id,
            type: { code: typeCode },
            member: { id: this.memberId },
            number: phoneNumber.number || null,
            extension: phoneNumber.extension || null,
            country: phoneNumber.country ? { id: phoneNumber.country?.id } : null
        };
        return savePhoneNumber;
    }

    protected parseTextContainer(text: CD.CustomFieldDefinitionTextContainer | undefined): CD.CustomFieldDefinitionTextContainer | undefined {
        if(!text) return text;
        const activeIndexes = new Set<number>();

        // Collect all active definitionIndexes from the text fields
        for(const column of this.customFieldTargetColumns?.text!) {
            if(column.isActive && column.definitionType === 'text') {
                activeIndexes.add(column.definitionIndex);
            }
        }

        let newText: CD.CustomFieldDefinitionTextContainer = {};

        Object.entries(text).forEach(([key, value]) => {
            //Match keys
            const match = key.match(/^value(\d{1,2})$/);
            if(match) {
                const index = parseInt(match[1], 10);
                if(activeIndexes.has(index)) {
                    newText[key as keyof CD.CustomFieldDefinitionTextContainer] = forceNull(value);
                }
            }
        });

        return newText;
    }

    // Strip extraneous fields from the UDF Lookup Values
    protected parseLookupContainer(lookup: CD.CustomFieldDefinitionLookupContainer | undefined): CD.CustomFieldDefinitionLookupContainer | undefined {
        if(!lookup) return lookup;
        const activeIndexes = new Set<number>();

        // Collect all active definitionIndexes from the text fields
        for(const column of this.customFieldTargetColumns?.lookup!) {
            if(column.isActive && column.definitionType === 'lookup') {
                activeIndexes.add(column.definitionIndex);
            }
        }

        let newLookup: CD.CustomFieldDefinitionLookupContainer = {};

        Object.entries(lookup).forEach(([fieldKey, fieldValue]) => {
            //Match keys
            const match = fieldKey.match(/^choice(\d{1,2})$/);
            if(match) {
                const index = parseInt(match[1], 10);
                if(activeIndexes.has(index)) {
                    newLookup = {
                        ...newLookup,
                        [fieldKey]: fieldValue?.code ? { code: fieldValue.code } : null
                    }
                }
            }
        });

        return newLookup;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadMember(): Promise<void> {
        if(!this.memberId) return;
        let memberAddresses = [];
        let member = null;

        const includes: string[] = [
            'custom',
            'demographics',
            'demographics.gender',
            'demographics.race',
            'ethnicity',
            'gender',
            'language.reads',
            'language.speaks',
            'maritalStatus',
            'phoneNumber.cell',
            'phoneNumber.fax',
            'phoneNumber.home',
            'phoneNumber.work',
            'race'
        ];

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMember(this.memberId, includes);
            member = response.results[0];
            if(response.results[0].address?.home?.isActive) memberAddresses.push(response.results[0].address?.home);
            if(response.results[0].address?.mailing?.isActive) memberAddresses.push(response.results[0].address?.mailing);
            if(response.results[0].address?.work?.isActive) memberAddresses.push(response.results[0].address?.work);

            //Set each object to their respective values
            this.saveMember = member;
            if(member.demographics === null) {
                member.demographics = { hasDurablePowerOfAttorney: false, hasAdvanceDirective: false };
            }
            else {
                if(member.demographics?.hasAdvanceDirective === null) {
                    member.demographics.hasAdvanceDirective = false;
                }
                if(member.demographics?.hasDurablePowerOfAttorney === null) {
                    member.demographics.hasDurablePowerOfAttorney = false;
                }
            }

            this.saveMemberDemographics = member.demographics || null;
            this.saveMemberAddresses = memberAddresses;
            this.saveMemberPhoneNumbers = member.phoneNumber || null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    // Get the Labels for Member Text and Lookup fields
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.Member', { definitionCount: this.memberUDFCount });
    }

    @apiCall
    private async loadMemberDefinitions() {
        const options: StandardLoadOptions = {
            all: true,
            includeInactive: true
        };
        try {
            this.state.beginLoad();
            const response = await this.customFieldService.loadCustomFields(this.searchFilters, options);
            this.memberUDFCount = response.results.length / 2; // 2 because each UDF has 2 definitions, one for text and one for lookup
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveMemberInformation(): Promise<boolean> {
        if(!this.saveMember) return false;
        let isSuccessful = false;
        const member = this.buildMember(this.saveMember)

        try {
            const response = await this.memberService.saveMember(member);
            this.memberId = response.id;
            isSuccessful = response.isSuccessful;
            // tell page header to refresh the member display only if the first or last name has changed
            if(this.originalMember?.name?.first !== member.name?.first || this.originalMember?.name?.last !== member.name?.last) {
                this.events.publish(new MemberDisplayChangeEvent());
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.updated('cd:member.$', isSuccessful);
            return isSuccessful;
        }
    }

    @apiCall
    private async saveDemographics(): Promise<boolean> {
        //return if there are no changes from the original object
        const propertyFilter = (propertyKey: string | number, target: any, propertyPath: string[]) => ["pronouns", "preferredName", "gender", "race", "disability", "hasDurablePowerOfAttorney", "hasAdvanceDirective", "advanceDirectiveExpirationDate"].includes(propertyKey.toString())
            || (propertyPath.includes('gender') && propertyKey === 'id')
            || (propertyPath.includes('race') && propertyKey === 'id');
        if(!this.saveMemberDemographics || !this.memberId || compareObjects(this.originalMember?.demographics, this.saveMemberDemographics, { propertyFilter }).areEqual) {
            return false
        }
        let isSuccessful = false;
        const memberDemographics = this.buildMemberDemographics(this.saveMemberDemographics)

        try {
            const response = await this.memberService.saveMemberDemographics(memberDemographics);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.updated('cd:member.demographics.$header', isSuccessful, 2);
            return isSuccessful;
        }
    }

    @apiCall
    private async savePhoneNumber(originalPhoneNumber: CD.MemberPhoneNumber, phoneNumber: CD.MemberPhoneNumber, code: string): Promise<boolean> {
        //Return if the number and extensions are equal. This had to be done in two seperate compares because of the way originalPhoneNumber gets passed in as an empty object.
        if(!this.memberId || compareObjects(originalPhoneNumber.number!, phoneNumber.number!, {}).areEqual && compareObjects(originalPhoneNumber.extension!, phoneNumber.extension!, {}).areEqual) {
            return false
        }
        let isSuccessful = false;
        const savePhoneNumber = this.buildPhoneNumber(phoneNumber, code);

        try {
            const response = await this.memberService.saveMemberPhoneNumber(savePhoneNumber);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.updated('cd:member.phoneNumber.' + code + '.$', isSuccessful);
            return isSuccessful;
        }
    }
}
